import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChartConfig } from '../../charts/types/chartTypes';
import type { QueryColumn } from '../../../../types/queryExecution';
import { requestPinToDashboard } from '../../dashboards/services/pinToDashboard';
import { registerPinCapture } from '../../common/services/pinCaptureRegistry';
import {
  registerRenameBufferEditor,
  unregisterRenameBufferEditor,
} from '../../../navigational-features/rename/renameBufferEditors';
import { QueryEditor } from './QueryEditor';
import type { QueryEditorHandle } from './QueryEditor';
import { QueryEditorTabDialogs } from './QueryEditorTabDialogs';
import { useQueryFileSync } from '../hooks/useQueryFileSync';
import { ResultsPanel } from '../../common/components/results-panel/ResultsPanel';
import { useResultsPanelState } from '../../common/components/results-panel/useResultsPanelState';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { QueryEditorTabBanners } from './QueryEditorTabBanners';
import { QueryStatusBar } from './QueryStatusBar';
import { QueryToolbar } from './QueryToolbar';
import { useQueryExecution } from '../hooks/useQueryExecution';
import type { WorkspaceTabComponentProps, QueryTab } from '../../core/types/workspaceTypes';
import { useExecutionActions } from '../hooks/useExecutionActions';
import { useRuntimeErrorMarker } from '../hooks/useRuntimeErrorMarker';
import { useTabConnectionState } from '../hooks/useTabConnectionState';
import { useQueryBufferSync } from '../hooks/useQueryBufferSync';
import { useEditorMountActions } from '../hooks/useEditorMountActions';
import { useAutoTabTitle } from '../hooks/useAutoTabTitle';
import { useCursorStatementTracking } from '../hooks/useCursorStatementTracking';
import { useStatementSegments } from '../hooks/useStatementSegments';
import { useSyntaxErrorSignal } from '../hooks/useSyntaxErrorSignal';
import { useEditorSelectionRequests } from '../hooks/useEditorSelectionRequests';
import type { IntelliSenseStatusType } from '../../common/utils/sqlMonacoTypes';
import { useConnectionSwitch } from '../hooks/useConnectionSwitch';
import { useResultsPaneDivider } from '../hooks/useResultsPaneDivider';
import type { TabEditorOptions } from '../../core/types/workspaceTypes';
import { useConnectionsUIStore } from '../../../connections/store/uiStore';
import { AiChatSidebar } from '../../../ai-chat/components/AiChatSidebar';
import { useAiChatSidebar } from '../hooks/useAiChatSidebar';
import { useAiChatSession } from '../../../ai-chat/hooks/useAiChatSession';
import { useAiTaskLauncher } from '../../../ai-chat/hooks/useAiTaskLauncher';
import { useAiTaskLaunchDrain } from '../../../ai-chat/hooks/useAiTaskLaunchDrain';
import { registerCursorStatementOffset } from '../utils/cursorStatementOffsets';
import { findSegmentAtOffset } from '../utils/statementAtCursor';
import { buildOptimizeQueryCtx, buildExplainErrorCtx } from '../utils/buildAiTaskCtx';
import type { QueryMessage } from '../../core/types/workspaceTypes';
import { useEnsureObjectsLoaded } from '../../../database-objects/hooks/useEnsureObjectsLoaded';
import { isFileOutOfDate } from '../utils/queryFileUtils';
import { useTabTransactions } from '../hooks/useTabTransactions';
import { useAppStore } from '../../../../store/appStore';
import { useAnalytics } from '../../../../core/analytics';
import { useResultsEditMode } from '../hooks/useResultsEditMode';
import { useResultsAddToWhere } from '../hooks/useResultsAddToWhere';
import { useResultsAddToFilter } from '../hooks/useResultsAddToFilter';
import { usePlanAndDebugActions } from '../hooks/usePlanAndDebugActions';
import { useQueryEditorShortcuts } from '../hooks/useQueryEditorShortcuts';
import { useIntelliSenseSchemaReload, INTELLISENSE_REFRESHING_MESSAGE } from '../hooks/useIntelliSenseSchemaReload';
import { useTabMcpCommands } from '../hooks/useTabMcpCommands';
import { QueryEditorOverlays } from './QueryEditorOverlays';
import { ResultsPaneDivider } from './ResultsPaneDivider';
import { useCompactEditAffordance } from '../hooks/useCompactEditAffordance';
import { useTableSeed } from '../table-seed/useTableSeed';
import { useVqeRunOnChange } from '../hooks/useVqeRunOnChange';
import { useVqeVisualRunOnChange } from '../hooks/useVqeVisualRunOnChange';
import { usePeekWiring } from '../hooks/usePeekWiring';
import { useBetaFeature } from '@/core/beta';
import { useConnectionPrewarm } from '../../common/hooks/useConnectionPrewarm';
import { useShortcutLabel } from '../../../keybindings/hooks/useShortcutLabel';
import { useQueryCancelKeybinding } from '../hooks/useQueryCancelKeybinding';
import { safeConnectionServer } from '@/types/safeConnectionDisplay';
import { useQueryViewMode, type QueryViewModeTarget } from '../visual-mode/useQueryViewMode';
import { registerQueryViewModeController } from '../visual-mode/queryViewModeControllers';
import { QueryVisualModePanel } from '../../visual-query-editor/mode/QueryVisualModePanel';
import { useVqeStore } from '../../visual-query-editor/store/vqeStore';
import { vqeCopy } from '../../visual-query-editor/ux/vqeCopy';
import type { QueryViewMode } from '../../core/types/workspaceTypes';
import {
  cancelTabTransactionBegin,
  isTabTransactionBeginInFlight,
  supersedeStaleTabTransactionBegin,
} from '../services/tabTransactionBegin';
import { resetTabSubmission } from '../services/tabSubmissionGate';

export function QueryEditorTabContent({ tabId, className = '', currentTab }: WorkspaceTabComponentProps & { currentTab: QueryTab }) {
  const updateTabContent = useWorkspaceStore((state) => state.updateTabContent);
  const activeTabId = useWorkspaceStore((state) => state.activeTabId);

  // Lazy language-service connection: only connect when the tab has been active at least once.
  // During session restore, all tabs mount simultaneously — this prevents a burst of concurrent
  // connection/connect IPC calls that can leave SQL Tools Service in a broken state.
  const isActive = activeTabId === tabId;
  const [hasBeenActive, setHasBeenActive] = useState(false);
  useEffect(() => { if (isActive) setHasBeenActive(true); }, [isActive]);

  const { track } = useAnalytics();
  const collapseShortcutHint = useShortcutLabel('query.toggleResults');

  const tabContent = currentTab.content;
  const connectionInfo = currentTab.connectionInfo;
  const connectionId = connectionInfo?.connectionId;
  const isGeneratingScript = tabContent?.isGeneratingScript ?? false;
  const filePath = tabContent?.filePath;
  const fileLastSavedHash = tabContent?.fileLastSavedHash;
  const isFileTied = typeof filePath === 'string' && filePath.trim().length > 0;

  // Ref to the QueryEditor for getting selected text
  const editorRef = useRef<QueryEditorHandle>(null);

  // NAV1 amendment (2026-08-23): after a DB-object rename, references in open
  // tabs are rewritten in place. Monaco model paths are per-mount nonces, so
  // the rewrite needs a tabId → editor-handle lookup to reach THIS tab's undo
  // stack (a store write would go through `useQueryBufferSync`'s external-change
  // effect, which calls `setValue` and discards undo history).
  useEffect(() => {
    registerRenameBufferEditor(tabId, {
      // `getValue` lets the rewrite verify the live model still holds the text
      // its byte offsets were planned against — the store copy lags behind by
      // the query-buffer debounce, so a stale splice would corrupt the buffer.
      // `SqlMonacoHandle.getValue` answers `''` while the Monaco chunk is still
      // loading, which is indistinguishable from an empty buffer — map it to
      // `undefined` ("cannot tell") so that state keeps its store fallback.
      getValue: () => editorRef.current?.getValue() || undefined,
      executeEdits: (source, edits) => editorRef.current?.executeEdits?.(source, edits) ?? false,
    });
    return () => unregisterRenameBufferEditor(tabId);
  }, [tabId]);

  // Query-buffer state machine: queryRef (synchronous truth), debounced React
  // `query` state + workspace-store flush, script-generation / external-store
  // sync, recent-query selection, and the AI-lock typing warning.
  const {
    query,
    setQuery,
    queryRef,
    lastStoredQueryRef,
    flushQueryDebounced,
    handleQueryChange,
    handleSelectRecentQuery,
  } = useQueryBufferSync({
    tabId,
    storeQuery: tabContent?.query,
    isGeneratingScript,
    editorRef,
  });

  // Tracks the SQL of the most recent execution when it was a partial run
  // (cursor-statement bar, selection bar, or F5 with a selection / cursor
  // statement). Null when the last execution ran the full buffer. Used by
  // `handleAddToWhere` so "Add to WHERE" edits the executed statement instead
  // of blindly appending to the end of the buffer when the active result set
  // came from one statement among many.
  const lastPartialExecutedSqlRef = useRef<string | null>(null);

  // AI Chat sidebar state (persisted per-tab in store) + CLI-required dialog
  // + parent-width observer for the sidebar's max width. The chat SESSION
  // (`useAiChatSession`) stays below — it needs connection info resolved later.
  const {
    isAiChatOpen,
    showCliRequiredDialog,
    setShowCliRequiredDialog,
    handleToggleAiChat,
    handleCloseAiChat,
    outerContainerRef,
    parentWidth,
  } = useAiChatSidebar(tabId);

  // Connection/session state: engine resolution, session-store-driven
  // disconnected detection, banner dismissal, reconnect, connection-lost.
  const {
    engineId,
    connectionColor,
    activeConnection,
    isDisconnected,
    bannerDismissed,
    setBannerDismissed,
    handleReconnect,
    handleConnectionLost,
  } = useTabConnectionState({
    tabId,
    connectionId,
    hasConnectionInfo: !!connectionInfo,
  });

  // SQL | Visual view of this tab — one buffer; Monaco stays mounted under
  // the Visual view, whose edits land on Monaco's own undo stack. The SQL
  // view's editor-anchored overlays are closed on the way in (the closer is
  // filled in once those hooks exist, below).
  const closeSqlOverlaysRef = useRef<() => void>(() => {});
  const closeSqlOverlays = useCallback(() => closeSqlOverlaysRef.current(), []);
  const viewMode = useQueryViewMode({
    tabId,
    viewMode: tabContent?.viewMode,
    visualStatementOffset: tabContent?.visualStatementOffset,
    engineId,
    query,
    editorRef,
    queryRef,
    flushQueryDebounced,
    closeSqlOverlays,
  });
  const { isVisual, isVisualRef, setViewMode } = viewMode;
  const handleViewModeChange = useCallback(
    (mode: QueryViewMode) => setViewMode(mode, 'query-toolbar'),
    [setViewMode],
  );
  const handleOpenVisualView = useCallback(
    (target: QueryViewModeTarget) => setViewMode('visual', 'compact-promote', target),
    [setViewMode],
  );
  // Lets the tab-less entry points (shortcut, palette, More ▾) switch this tab.
  useEffect(
    () => registerQueryViewModeController(tabId, { isVisual: () => isVisualRef.current, setViewMode }),
    [tabId, isVisualRef, setViewMode],
  );

  // Mount-time editor actions: bounded-retry snippet insertion ("Script
  // New..."), the queued find-references reveal, and focus-on-activation.
  useEditorMountActions({
    tabId,
    activeTabId,
    insertSnippetOnMount: tabContent?.insertSnippetOnMount,
    storeQuery: tabContent?.query,
    isAiChatOpen,
    editorHidden: isVisual,
    editorRef,
  });

  // Auto-update tab title when query changes (debounced, only if not custom title)
  useAutoTabTitle({
    tabId,
    query,
    isCustomTitle: currentTab.customTitle ?? false,
    isFileTied,
  });

  // DOM anchor scoped to just this tab's Monaco mount (the "Top section -
  // Query Editor" wrapper below — NOT the results pane). `findOwnEditor`
  // above uses it to identify this tab's own `ICodeEditor`/model among every
  // other open tab's, without `SqlMonaco.tsx` exposing its model URI.
  const sqlEditorContainerRef = useRef<HTMLDivElement>(null);
  // Ref for closing peek results (used in selection change handler before hook is declared)
  const closePeekRef = useRef<(() => void) | null>(null);

  // Pin capture (F5 / Task 20; corrected under Codex gate-3 F1) — expose this
  // query tab's LIVE definition to the pin button + tab-strip pin menu. The
  // SQL is read from Monaco's synchronous truth (`editorRef.getValue()`,
  // falling back to `queryRef.current`), NEVER the debounced React `query`
  // state or the `tabContent.query` store copy: a pin taken immediately after
  // a keystroke must reflect what's on screen (F5).
  // ALWAYS returns a text snapshot — this capture never branches on
  // `filePath`. The file-vs-snapshot decision (G4) is made ONCE, at creation,
  // in `buildPinnedWorkspaceRef` (from `filePathOf(tab)`); if the capture also
  // flipped to `source:'file'` after the tab's first Save, an existing
  // text-snapshot pin would (a) show a false "diverged" dot (the stored text
  // payload no longer matches a captured file payload) and (b) get
  // permanently converted to a file pin on the next Update — see
  // `codex-gate3-fixes-brief.md` F1.
  useEffect(
    () =>
      registerPinCapture(tabId, () => {
        const sql = editorRef.current?.getValue() ?? queryRef.current ?? '';
        return { workspaceType: 'query', payload: { source: 'text', query: sql } };
      }),
    [tabId, queryRef],
  );

  // Capture whether `queryText` is a partial execution (subset of the current
  // buffer). Called by every execution entry point so the partial-SQL ref
  // stays in sync. Declared up here so it's initialized before any callback
  // that might reference it.
  const trackExecutionScope = useCallback((queryText: string) => {
    const fullBuffer = (editorRef.current?.getValue() ?? queryRef.current ?? '').trim();
    const executed = queryText.trim();
    lastPartialExecutedSqlRef.current =
      executed && executed !== fullBuffer ? queryText : null;
    // The SQL the results pane is about to show, from every run path (Peek →
    // Open in Results included): the results refresh after a save, result and
    // dashboard pins, and the Visual view's stale notice all read it.
    useVqeStore.getState().setLastExecuted(tabId, queryText);
  }, [queryRef, tabId]);

  // Version-keyed statement-segments cache: caret moves reuse the last parse
  // (and the last getValue() string) until the buffer actually changes. Owned
  // here so the selection handler and the F5 shortcut share one cache.
  const getStatementSegments = useStatementSegments(editorRef, engineId);

  // Selection + cursor-statement tracking: the floating-bar selection state,
  // the AI selection mirror, `currentCursorStatement` (pencil-reachable gate),
  // the ungated `rawCursorSegment` (broken-statement affordance), the Monaco
  // current-statement highlight, and the F5 refs mirror.
  const {
    editorSelectedText,
    editorSelectedRange,
    currentCursorStatement,
    rawCursorSegment,
    selectedStatement,
    editorSelectedTextRef,
    currentCursorStatementRef,
    handleEditorSelectionChange,
  } = useCursorStatementTracking({
    tabId,
    tabTitle: currentTab.title,
    engineId,
    editorRef,
    getStatementSegments,
    closePeekRef,
  });

  // Let the toolbar Visual button / Ctrl+Shift+V / Command Palette open the
  // Visual Query Editor on the statement under the caret. Read live at action
  // time like the execute paths (selection-change tracking is debounced), and
  // ungated, so a statement that does not parse opens on its own parse-error view.
  useEffect(
    () =>
      registerCursorStatementOffset(tabId, () => {
        const offset = editorRef.current?.getCursorOffset();
        const parsed = getStatementSegments();
        if (offset == null || !parsed) return undefined;
        return findSegmentAtOffset(parsed.segments, offset)?.trimStart;
      }),
    [tabId, getStatementSegments],
  );

  // Track IntelliSense status for the status bar indicator
  const [intelliSenseStatus, setIntelliSenseStatus] = useState<IntelliSenseStatusType>('unavailable');
  const [intelliSenseMessage, setIntelliSenseMessage] = useState<string | undefined>();

  // Handle IntelliSense status changes from the editor
  const handleIntelliSenseStatusChange = useCallback((status: IntelliSenseStatusType, message?: string) => {
    setIntelliSenseStatus(status);
    // Defensive: IPC data may pass non-string values at runtime
    setIntelliSenseMessage(typeof message === 'string' ? message : undefined);
  }, []);

  // "Reload schema" behind the pill's `partial` state (S7 item 5) — forces
  // the engine's schema cache to refetch after a row cap clipped part of it.
  const { reloadSchema, isReloadingSchema } = useIntelliSenseSchemaReload({
    connectionId,
    database: connectionInfo?.database,
    engineId,
    onStatusChange: handleIntelliSenseStatusChange,
  });

  // Apply externally-requested editor selections (AI / MCP selection bus).
  useEditorSelectionRequests({ tabId, editorRef });

  // Get the database from connection info
  const tabDatabase = connectionInfo?.database;

  // Open a connection ahead of the user's first query (remote pools hold no
  // standing connection by design). Fired on editor focus and on typing; both
  // are throttled inside the hook. No UI surface, so no analytics event.
  const notePrewarmIntent = useConnectionPrewarm(connectionId, tabDatabase);
  // Typing is the strongest "a query is coming" signal there is. Throttled
  // inside the hook, so this stays a Map lookup on the keystroke path. Stable
  // identity (deps change only with the lock/prewarm target), so QueryEditor's
  // React.memo still skips on the typing path.
  const handleQueryChangeWithPrewarm = useCallback((newQuery: string) => {
    notePrewarmIntent();
    handleQueryChange(newQuery);
    // The Visual view re-renders on every buffer change: nobody types there,
    // so the keystroke debounce saves nothing.
    if (isVisualRef.current) flushQueryDebounced.flush();
  }, [notePrewarmIntent, handleQueryChange, isVisualRef, flushQueryDebounced]);

  // Database objects for @-mention autocomplete and the "Query a table" picker
  // (loads on demand if not cached; a failed load is reported, not left pending)
  const {
    objects: databaseObjects,
    failure: databaseObjectsFailure,
    retry: retryDatabaseObjects,
  } = useEnsureObjectsLoaded(connectionId, tabDatabase, engineId);

  // AI Chat session (send messages to Claude CLI)
  // T13 — dedicated AI task launcher instance for "AI: Optimize this query"
  // (toolbar) and "AI: Explain this error" (Messages panel). Separate from the
  // AI Chat sidebar's own CLI detection: `useAiTaskLauncher` applies the same
  // CLI gate but also resolves the host tab and enqueues the launcher prompt.
  const {
    launchTask,
    cliAvailable: aiTaskCliAvailable,
    cliDetectionLoading: aiTaskCliDetectionLoading,
    cliRequiredOpen: aiTaskCliRequiredOpen,
    setCliRequiredOpen: setAiTaskCliRequiredOpen,
  } = useAiTaskLauncher();

  // ctx.tabId is passed explicitly (per F0.4's amendment) so the launcher
  // always targets THIS tab's buffer/connection, never resolving to a sibling
  // tab that happens to share the connection. Built via the extracted pure
  // functions (not an inline object literal) so the invariant is pinned by a
  // unit test on the builder itself — see buildAiTaskCtx.test.ts.
  const handleOptimizeQuery = useCallback(() => {
    if (!connectionId) return;
    launchTask(
      'optimize-query',
      buildOptimizeQueryCtx(connectionId, tabDatabase, tabId),
      'query-editor'
    );
  }, [launchTask, connectionId, tabDatabase, tabId]);

  // One-click next step on a classified error's tip row (queryErrorAction.ts):
  // 'switch_database' pops the status bar's database selector open (nonce-
  // signalled through QueryStatusBar), 'open_session_browser' opens a Session
  // Browser tab on this tab's connection so the blocking session is findable.
  const [databaseSelectorOpenNonce, setDatabaseSelectorOpenNonce] = useState(0);
  const handleMessageAction = useCallback(
    (message: QueryMessage) => {
      const action = message.action;
      if (!action || !connectionId) return;
      track('feature:query_error_fix_clicked', { kind: action.kind, engine: engineId });
      if (action.kind === 'switch_database') {
        setDatabaseSelectorOpenNonce((n) => n + 1);
        return;
      }
      useWorkspaceStore.getState().addTab({
        title: `Sessions: ${connectionInfo?.connectionName ?? 'connection'}`,
        type: 'session-browser',
        connectionInfo: {
          connectionId,
          connectionName: connectionInfo?.connectionName,
          serverHost: connectionInfo?.serverHost,
          database: connectionInfo?.database,
        },
        content: { filter: 'active', autoRefreshInterval: 0, isLoading: true },
      });
    },
    [connectionId, connectionInfo?.connectionName, connectionInfo?.serverHost, connectionInfo?.database, engineId, track]
  );

  const handleExplainError = useCallback(
    (message: QueryMessage) => {
      if (!connectionId) return;
      launchTask(
        'explain-error',
        buildExplainErrorCtx(connectionId, tabDatabase, tabId, message.content),
        'query-editor'
      );
    },
    [launchTask, connectionId, tabDatabase, tabId]
  );

  const aiChatSession = useAiChatSession({
    tabId,
    tabType: 'query',
    engine: engineId,
    serverHost: safeConnectionServer(activeConnection?.connection),
    database: tabDatabase,
    connectionId,
    hasConnection: !!connectionId && !isDisconnected,
    getEditorContent: useCallback(() => editorRef.current?.getValue() ?? '', []),
    getSelectedText: useCallback(() => editorRef.current?.getSelectedText() ?? '', []),
  });

  // AI task launchers fired from surfaces with no chat session of their own
  // (Object Explorer, Schema Overview, compare results) park their prompt in
  // `aiTaskLaunchStore` for this tab; `sendMessage` only exists here, so the
  // tab drains the queue. Entries queued before this tab mounted are picked up
  // from the store snapshot read at mount.
  useAiTaskLaunchDrain({ tabId, sendMessage: aiChatSession.sendMessage });

  // Per-tab editor options (word wrap, format on execute, result display mode)
  const editorOptions = tabContent?.editorOptions;
  const formatOnExecute = editorOptions?.formatOnExecute ?? false;
  const resultDisplayMode = editorOptions?.resultDisplayMode ?? 'grid';

  // Global preference: show the floating Execute/Peek bar (next to selections
  // AND above the cursor's statement). When off, no floating bar — selections
  // run via the toolbar (label changes to "Selection") and the cursor's
  // statement gets two small icon buttons stacked in the gutter to the left
  // of the line numbers (`CursorStatementGutterActions`).
  const showInlineExecuteBar = useAppStore(
    (state) => state.editorSettings.showInlineExecuteBar,
  );

  // Never-silent parse-error signal (VQE Polish, EDG-11..15): LSP/MSSQL
  // markers (A) + the conservative local tree-sitter predicate (B) compose
  // into the broken-statement affordance. `affordanceStatement` is the
  // pencil-reachable statement, else the confirmed-broken raw segment.
  const {
    hasMarkerSyntaxError,
    hasLocalSyntaxError,
    affordanceStatement,
  } = useSyntaxErrorSignal({
    engineId,
    sqlEditorContainerRef,
    currentCursorStatement,
    rawCursorSegment,
  });

  // The statement the pencil edits: the one under the caret, else the one a
  // selection covers exactly.
  const editStatement = affordanceStatement ?? selectedStatement;

  // Minimal visual-edit affordance (WS2): the [✎] pencil / gutter icon and
  // the Compact VQE popover it opens — live-classified `cursorEditInfo`, the
  // `vqe-parse` squiggle, and the popover open state.
  const {
    vqeParseOptions,
    cursorEditInfo,
    compactEdit,
    setCompactEdit,
    openCompactEdit,
  } = useCompactEditAffordance({
    engineId,
    query,
    isActive,
    editorRef,
    queryRef,
    affordanceStatementRange: editStatement?.range ?? null,
    hasMarkerSyntaxError,
    hasLocalSyntaxError,
  });

  // Handle editor options change (updates both tab and global defaults via QueryToolbar)
  const handleEditorOptionsChange = useCallback((options: Partial<TabEditorOptions>) => {
    updateTabContent(tabId, {
      editorOptions: {
        wordWrap: editorOptions?.wordWrap ?? 'on',
        formatOnExecute: editorOptions?.formatOnExecute ?? false,
        ...options,
      },
    });
  }, [tabId, updateTabContent, editorOptions]);

  // Monaco runtime-error squiggles ('sql-runtime-error' owner) from the
  // structured errorLocation an engine adapter returns after a failed run.
  const { handleErrorLocation } = useRuntimeErrorMarker({ editorRef });

  // Query execution hook (uses V2 API with auto-generated executionId)
  const {
    isExecuting,
    isCancelling,
    isLongRunning,
    executionTime,
    hasExecuted,
    rowCount,
    totalRowCount,
    isTruncated,
    affectedRowCount,
    lastMessage,
    setStatusMessage,
    executeCurrentQuery,
    cancelCurrentQuery
  } = useQueryExecution({
    tabId,
    connectionId,
    database: tabDatabase,
    engineId,
    onSuccess: useCallback((hasResults: boolean) => {
      // Always refocus to results/messages — never stay on a pinned tab after execution
      setActiveTab(hasResults ? 'results' : 'messages');
    }, []),
    onError: useCallback(() => setActiveTab('messages'), []),
    onConnectionLost: handleConnectionLost,
    onErrorLocation: handleErrorLocation,
  });

  // Results-pane state (active tab, collapse/zoom, result-set derivations, the
  // result-cache IPC effects, population progress, pin/unpin) — extracted into
  // the shared hook so the same panel powers the Query Editor and the Visual
  // Query Editor. Called here (the host) so the ~15 tab-switch/collapse writers
  // below keep their exact code shape via these destructured local names.
  // A pin records the SQL that produced the results (recorded for every run,
  // in either view) — not the buffer as it reads now.
  const getPinnedQueryText = useCallback(
    () => useVqeStore.getState().lastExecuted[tabId] ?? queryRef.current,
    [queryRef, tabId],
  );

  // S20 — pin to dashboard: the host owns the provenance (executed SQL from
  // the live buffer, connection, database); the pane adds per-surface extras
  // (result shape, chart config). No connection → surfaces hide the action.
  // Pin to dashboard is a Dashboards surface — it disappears with the
  // feature rather than pinning into a workspace the user can't open.
  const dashboardsBetaEnabled = useBetaFeature('dashboards');
  const plSqlDebuggerBetaEnabled = useBetaFeature('pl-sql-debugger');

  const handlePinToDashboard = useCallback(
    (extras: {
      from: 'qe-results' | 'qe-chart' | 'plan';
      chartConfig?: ChartConfig;
      shape?: { columns: QueryColumn[]; rows: Record<string, unknown>[] };
    }) => {
      if (!connectionId) return;
      requestPinToDashboard({
        ...extras,
        // Results and charts come from the SQL that ran; a plan pin keeps the
        // plan's own query (Explain never executes).
        sql: extras.from === 'plan' ? queryRef.current : getPinnedQueryText(),
        connectionId,
        database: tabDatabase,
      });
    },
    [connectionId, tabDatabase, getPinnedQueryText, queryRef],
  );

  const resultsPanelState = useResultsPanelState({
    tabId,
    content: tabContent,
    isExecuting,
    getPinnedQueryText,
    engineId,
  });
  const {
    activeTab,
    setActiveTab,
    collapsed: bottomPaneCollapsed,
    setCollapsed: setBottomPaneCollapsed,
    scrollRow,
    isWindowed,
    activeRowCount,
    populationEntry,
    activeResultSet,
  } = resultsPanelState;

  // Peek Results wiring: popup state, "Open in Results" promotion, the
  // closePeekRef mirror, and the close-on-execute/-deactivate effects.
  const {
    isPeekExecuting,
    handlePeek,
    closePeek,
    peekState,
    peekAnchor,
    handleOpenPeekInResultsAndClose,
  } = usePeekWiring({
    tabId,
    connectionId,
    database: tabDatabase,
    engineId,
    isExecuting,
    isActive,
    editorRef,
    queryRef,
    lastStoredQueryRef,
    setQuery,
    closePeekRef,
    trackExecutionScope,
    executeCurrentQuery,
  });

  // Transaction wiring: toolbar transaction prop block, connection-drop
  // release, prepareForExecution (auto-begin), and the transaction-guarded
  // database-switch confirmation.
  const {
    transactionCommitEnabled,
    transactionRollbackEnabled,
    transactionModeChangeDisabled,
    transactionIsolationLevelState,
    handleTransactionModeChange,
    handleTransactionIsolationLevelChange,
    handleTransactionCommit,
    handleTransactionRollback,
    prepareForExecution,
    showTransactionControls,
    pendingDatabaseChange,
    setPendingDatabaseChange,
    handleDatabaseChange,
    handleConfirmDatabaseChange,
  } = useTabTransactions({
    tabId,
    transactionMode: tabContent.transactionMode,
    transactionState: tabContent.transactionState,
    transactionSessionGeneration: tabContent.transactionSessionGeneration,
    transactionBeginPending: tabContent.transactionBeginToken !== undefined,
    isolationLevel: tabContent.isolationLevel,
    connectionId,
    database: tabDatabase,
    engineId,
    isDisconnected,
    isActiveTab: activeTabId === tabId,
  });

  // `useTabTransactions` rebuilds this small object every render; pin its
  // identity to its fields so it doesn't defeat QueryToolbar's memo.
  const stableTransactionIsolationLevelState = useMemo(
    () => transactionIsolationLevelState,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      transactionIsolationLevelState.visible,
      transactionIsolationLevelState.disabled,
      transactionIsolationLevelState.activeTransaction,
    ],
  );

  // Resizable editor/results split: dragged topHeight %, container ref, the
  // divider's separator handlers, and the fixed layout heights the styles use.
  const {
    topHeight,
    containerRef,
    handlePointerDown,
    handleDoubleClick: handleDividerDoubleClick,
    handleKeyDown: handleDividerKeyDown,
    isDragging: isDividerDragging,
    dividerHeight,
    tabsBarHeight,
    statusBarHeight,
  } = useResultsPaneDivider({ bottomPaneCollapsed, setBottomPaneCollapsed });

  // Execution orchestration: every run path (toolbar / keyboard / floating
  // bars / MCP / auto-execute-on-mount) funnels through the dangerous-query
  // gate into doExecuteQuery (debounce flush + transaction auto-begin).
  const {
    dangerInfo,
    dangerousDialogOpen,
    handleDangerousConfirm,
    handleDangerousCancel,
    handleExecute,
    handleExecuteFromToolbar,
    handleResultsGridReExecute,
    handleExecuteSelection,
    handleExecuteFromSelectionBar,
    handleExecuteFromCursorBar,
    handleExecuteFromToolbarSelection,
    handleExecuteWithActualPlan,
    handleReExecuteAfterSave,
    runVisualStatement,
  } = useExecutionActions({
    tabId,
    engineId,
    connectionId,
    tabDatabase,
    storeQuery: tabContent?.query,
    autoExecuteOnMount: tabContent?.autoExecuteOnMount,
    query,
    formatOnExecute,
    editorRef,
    queryRef,
    flushQueryDebounced,
    prepareForExecution,
    executeCurrentQuery,
    trackExecutionScope,
    resolveVisualRun: viewMode.resolveVisualRun,
  });

  // "Query a table" seed flow: the blank-line gutter glyph / palette entry,
  // the anchored table picker, and the seed → run → open-compact-VQE handler.
  const tableSeed = useTableSeed({
    isActive,
    engineId,
    connectionId,
    database: tabDatabase,
    editorRef,
    databaseObjects,
    databaseObjectsFailure,
    retryDatabaseObjects,
    isExecuting,
    isGeneratingScript,
    executeStatement: handleExecuteFromCursorBar,
    openCompactEdit,
  });

  // "Run on change": re-run the SELECT under the compact VQE popover after
  // each committed visual edit (setting-gated inside the popover header).
  // Shared with the Visual view below — it is the same user preference
  // ("re-run my edits as I make them"), not a per-surface one: an on/off
  // split would mean the same tab shows stale results in one VQE surface and
  // live ones in the other for the exact same toggle the user just set.
  const vqeRunOnChange = useAppStore((state) => state.visualQueryEditorSettings.runOnChange);
  const setVisualQueryEditorSettings = useAppStore((state) => state.setVisualQueryEditorSettings);
  useVqeRunOnChange({
    active: compactEdit.open && vqeRunOnChange && !isVisual,
    mode: cursorEditInfo.mode,
    engineId,
    editorRef,
    getStatementSegments,
    executeStatement: handleExecuteFromCursorBar,
  });
  // Same setting, the Visual view's own shown statement as the target
  // (`resolveVisualRun` — the exact source a manual Execute click runs).
  const runVisualStatementOnChange = useCallback(
    () => runVisualStatement('run-on-change'),
    [runVisualStatement],
  );
  useVqeVisualRunOnChange({
    active: isVisual && isActive && vqeRunOnChange,
    engineId,
    resolveVisualRun: viewMode.resolveVisualRun,
    executeStatement: runVisualStatementOnChange,
  });
  const handleVisualRunOnChangeToggle = useCallback(() => {
    setVisualQueryEditorSettings({ runOnChange: !vqeRunOnChange });
    track('feature:vqe_run_on_change_toggled', { enabled: !vqeRunOnChange, source: 'visual' });
  }, [vqeRunOnChange, setVisualQueryEditorSettings, track]);

  closeSqlOverlaysRef.current = () => {
    setCompactEdit((prev) => (prev.open ? { ...prev, open: false } : prev));
    closePeek();
    tableSeed.setOpen(false);
  };

  // A restored tab under the same id (session restore — the React key does
  // not change) starts free: a BEGIN or run submission left by the previous
  // instance lost ownership with it. A live instance's own BEGIN always carries
  // its token in the tab content, so "no token, yet something in flight" can
  // only be the previous instance's. Runs on mount and on every restore of
  // THIS tab — never for another tab's restore.
  const restoreGeneration = useWorkspaceStore((state) => state.tabRestoreGenerations[tabId] ?? 0);
  useEffect(() => {
    if (tabContent.transactionBeginToken !== undefined) return;
    if (isTabTransactionBeginInFlight(tabId)) supersedeStaleTabTransactionBegin(tabId);
    resetTabSubmission(tabId);
    // Keyed on the restore, not on every content change of this tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId, restoreGeneration]);

  // A BEGIN on the wire counts as running: Stop shows (and gives it up,
  // `cancelTabTransactionBegin`) and Execute is not offered until it settles.
  const transactionBeginPending = tabContent.transactionBeginToken !== undefined;
  const isRunBusy = isExecuting || transactionBeginPending;

  // Stable toolbar Stop handler — an inline arrow here would defeat
  // QueryToolbar's memo on every QueryEditorTabContent render.
  const handleStopFromToolbar = useCallback(() => {
    if (cancelTabTransactionBegin(tabId)) {
      track('feature:transaction_begin_cancel', { engine: engineId });
      return;
    }
    return cancelCurrentQuery({ source: 'toolbar' });
  }, [tabId, engineId, track, cancelCurrentQuery]);

  useQueryCancelKeybinding({
    tabId,
    editorRef,
    rootRef: outerContainerRef,
    isExecuting: isRunBusy,
    onCancel: handleStopFromToolbar,
  });

  // Status-bar connection switch: repoint connectionInfo, discard result
  // sources, clear/regenerate the cache, keep a same-named database.
  const { handleConnectionChange } = useConnectionSwitch({ tabId, setActiveTab });

  // A results-grid Save is a write: it auto-begins like Execute of an UPDATE.
  const prepareForResultsSave = useCallback(
    () => prepareForExecution(undefined, { writes: true }),
    [prepareForExecution],
  );

  // Results-grid edit mode: results-editing wiring, the stable editMode prop,
  // the edit-preview dialog and Open-in-Query-Editor.
  const {
    resultsEditing,
    editModeProp,
    isPreviewDialogOpen,
    setIsPreviewDialogOpen,
    handleOpenInQueryEditor,
  } = useResultsEditMode({
    tabId,
    engineId,
    connectionId,
    database: tabDatabase,
    connectionInfo,
    activeResultSetColumns: activeResultSet?.columns,
    isWindowed,
    onSaveComplete: handleReExecuteAfterSave,
    prepareForSave: prepareForResultsSave,
  });

  // Results-grid Add to WHERE: a condition in the statement (SQL view) or a
  // pre-filled condition draft (Visual view).
  const handleAddToWhere = useResultsAddToWhere({
    tabId,
    engineId,
    connectionId,
    database: tabDatabase,
    isVisual,
    editorRef,
    lastPartialExecutedSqlRef,
  });

  // Results-grid column header "Add to Filter": the column's condition editor
  // in the Visual view, or in the compact popover on the results' statement.
  const addColumnToFilter = useResultsAddToFilter({
    tabId,
    engineId,
    isVisual,
    visualStatementOffset: viewMode.statementOffset,
    editorRef,
    lastPartialExecutedSqlRef,
    openCompactEdit,
  });

  // Handle format SQL button
  const handleFormat = useCallback(() => {
    editorRef.current?.formatSql();
  }, []);

  // File-sync surface: Open / Save / Save As, tab-title-from-file-name, and
  // the external-file-change prompt (reload vs keep).
  const {
    isOpeningFile,
    isSavingFile,
    handleOpenFile,
    handleSaveFile,
    handleSaveFileAs,
    pendingChange,
    handleExternalReload,
    handleExternalKeep,
  } = useQueryFileSync({
    tabId,
    filePath,
    pendingFileReload: tabContent?.pendingFileReload,
    tabTitle: currentTab.title,
    connectionId: connectionInfo?.connectionId,
    connectionName: connectionInfo?.connectionName,
    editorRef,
    queryRef,
    lastStoredQueryRef,
    setQuery,
  });

  // Window-level shortcuts for the active tab: F5 / Shift+F5 execute,
  // Cmd/Ctrl+S / Cmd/Ctrl+Shift+S save, Cmd/Ctrl+J + the command-palette
  // event toggle the results pane.
  useQueryEditorShortcuts({
    tabId,
    activeTabId,
    engineId,
    editorRef,
    rootRef: outerContainerRef,
    queryRef,
    editorSelectedTextRef,
    currentCursorStatementRef,
    getStatementSegments,
    handleExecute,
    handleExecuteSelection,
    runVisualStatement,
    handleSaveFile,
    handleSaveFileAs,
    setStatusMessage,
    setBottomPaneCollapsed,
  });

  // Estimated-plan capture + PL/SQL debug launch (Oracle only).
  const {
    isExplainingPlan,
    isOracle,
    isPlsql,
    handleDebugPlsql,
    handleExplainPlan,
  } = usePlanAndDebugActions({
    tabId,
    engineId,
    connectionId,
    database: tabDatabase,
    connectionInfo,
    query,
    editorRef,
    queryRef,
    setActiveTab,
  });

  // MCP command consumption for query editor tabs (ui_control tools).
  useTabMcpCommands({
    tabId,
    activeTab,
    setActiveTab,
    chartConfigs: tabContent?.chartConfigs,
    editorRef,
    queryRef,
    handleExplainPlan,
    handleExecuteWithActualPlan,
  });

  const isFileDirty = isFileTied && isFileOutOfDate({ query, fileLastSavedHash });
  // What the results show — the Visual view marks them stale once its
  // statement no longer matches.
  const lastExecutedSql = useVqeStore((state) => state.lastExecuted[tabId]);

  return (
    <div
      ref={outerContainerRef}
      className={`h-full flex flex-col overflow-hidden ${className}`}
      data-testid="query-editor-tab"
    >
      {/* Banner strip: disconnected-connection banner, rename courtesy
          notices (Slice 34), per-tab connection-failure banner (Slice 6). */}
      <QueryEditorTabBanners
        tabId={tabId}
        connectionId={connectionId}
        tabDatabase={tabDatabase}
        connectionName={connectionInfo?.connectionName}
        isDisconnected={isDisconnected}
        bannerDismissed={bannerDismissed}
        setBannerDismissed={setBannerDismissed}
        handleReconnect={handleReconnect}
        connectionFailure={tabContent.connectionFailure}
      />

      {/* Query Toolbar - auto height, allows wrapping */}
      <div className="flex-shrink-0">
        <QueryToolbar
          tabId={tabId}
          onExecute={handleExecuteFromToolbar}
          // The Visual view runs its shown statement — a selection left in the
          // hidden editor must not arm "Selection".
          onExecuteSelection={isVisual ? undefined : handleExecuteFromToolbarSelection}
          selectedText={isVisual ? '' : editorSelectedText}
          executeScope={isVisual ? 'statement' : undefined}
          viewMode={isVisual ? 'visual' : 'sql'}
          onViewModeChange={handleViewModeChange}
          onExecuteWithActualPlan={handleExecuteWithActualPlan}
          onStop={handleStopFromToolbar}
          onFormat={isVisual ? undefined : handleFormat}
          onExplainPlan={handleExplainPlan}
          onOpenFile={handleOpenFile}
          onSaveFile={handleSaveFile}
          onSaveFileAs={isFileTied ? handleSaveFileAs : undefined}
          isExecuting={isRunBusy}
          isCancelling={isCancelling}
          isExplainingPlan={isExplainingPlan}
          isOpeningFile={isOpeningFile}
          isSavingFile={isSavingFile}
          disabled={!connectionId}
          isDisconnected={isDisconnected}
          editorOptions={editorOptions}
          onEditorOptionsChange={handleEditorOptionsChange}
          connectionId={connectionId}
          database={tabDatabase}
          onSelectRecentQuery={handleSelectRecentQuery}
          transactionMode={showTransactionControls ? tabContent.transactionMode : undefined}
          transactionState={tabContent.transactionState}
          transactionIsolationLevel={tabContent.isolationLevel}
          onTransactionModeChange={handleTransactionModeChange}
          onTransactionIsolationLevelChange={handleTransactionIsolationLevelChange}
          onTransactionCommit={handleTransactionCommit}
          onTransactionRollback={handleTransactionRollback}
          transactionCommitEnabled={transactionCommitEnabled}
          transactionRollbackEnabled={transactionRollbackEnabled}
          transactionModeChangeDisabled={transactionModeChangeDisabled}
          transactionIsolationLevelState={stableTransactionIsolationLevelState}
          isAiChatOpen={isAiChatOpen}
          onToggleAiChat={handleToggleAiChat}
          isAnonymousPlsqlBlock={isPlsql}
          onDebug={isOracle && plSqlDebuggerBetaEnabled ? handleDebugPlsql : undefined}
          onOptimizeQuery={connectionId ? handleOptimizeQuery : undefined}
          aiCliAvailable={aiTaskCliAvailable}
          aiCliDetectionLoading={aiTaskCliDetectionLoading}
        />
      </div>

      {/* Main content area: editor+results column + optional AI sidebar */}
      <div className="flex-1 min-h-0 flex overflow-hidden">
        {/* Editor + Results column */}
        <div ref={containerRef} className="flex-1 min-w-0 flex flex-col overflow-hidden">
          {/* Top section - Query Editor */}
          <div
            ref={sqlEditorContainerRef}
            className="relative flex-shrink-0 overflow-hidden"
            // React's onFocus is focusin, so Monaco's inner textarea taking
            // focus bubbles here — no prop needed on QueryEditor itself.
            onFocus={notePrewarmIntent}
            style={{
              height: bottomPaneCollapsed
                ? `calc(100% - ${dividerHeight}px - ${tabsBarHeight}px - ${statusBarHeight}px)`
                : `calc(${topHeight}% - ${dividerHeight / 2}px)`,
            }}
          >
            {/* The Visual view hides the editor (display:none — unfocusable,
                and its keybinding targets report it invisible) but keeps it
                mounted: same model, same undo history. */}
            <QueryEditor
              ref={editorRef}
              initialQuery={query}
              onChange={handleQueryChangeWithPrewarm}
              onSelectionChange={handleEditorSelectionChange}
              engineId={engineId}
              connectionId={hasBeenActive ? connectionId : undefined}
              database={hasBeenActive ? tabDatabase : undefined}
              className={isVisual ? 'absolute inset-0 hidden' : 'absolute inset-0'}
              wordWrap={editorOptions?.wordWrap}
              onIntelliSenseStatusChange={handleIntelliSenseStatusChange}
            />
            {isVisual && (
              <QueryVisualModePanel
                tabId={tabId}
                editorRef={editorRef}
                query={query}
                statementOffset={viewMode.statementOffset}
                engineId={engineId}
                connectionId={connectionId}
                database={tabDatabase}
                isActivePane={isActive}
                focusOnActivate={!isAiChatOpen}
                isExecuting={isExecuting}
                dangerDialogOpen={dangerousDialogOpen}
                onExecute={(source) => runVisualStatement(source)}
                runOnChange={{ enabled: vqeRunOnChange, onToggle: handleVisualRunOnChangeToggle }}
                onSelectStatement={viewMode.selectStatement}
                onEditAsSql={(errorOffset) => setViewMode('sql', 'edit-as-sql', { errorOffset })}
                onChooseDatabase={() => setDatabaseSelectorOpenNonce((n) => n + 1)}
                className="absolute inset-0"
              />
            )}
            {/* Overlay layer over the Monaco mount: floating Execute/Peek
                bars (or gutter fallback), Peek Results popup, Compact VQE
                popover, inline DDL toolbar, script-generation overlay. */}
            {!isVisual && <QueryEditorOverlays
              tabId={tabId}
              engineId={engineId}
              connectionId={connectionId}
              tabDatabase={tabDatabase}
              editorRef={editorRef}
              query={query}
              showInlineExecuteBar={showInlineExecuteBar}
              editorSelectedText={editorSelectedText}
              editorSelectedRange={editorSelectedRange}
              isExecuting={isRunBusy}
              isPeekExecuting={isPeekExecuting}
              isGeneratingScript={isGeneratingScript}
              affordanceStatement={affordanceStatement}
              editStatement={editStatement}
              currentCursorStatement={currentCursorStatement}
              rawCursorSegment={rawCursorSegment}
              tableSeed={tableSeed}
              cursorEditInfo={cursorEditInfo}
              vqeParseOptions={vqeParseOptions}
              compactEdit={compactEdit}
              setCompactEdit={setCompactEdit}
              openCompactEdit={openCompactEdit}
              peekState={peekState}
              peekAnchor={peekAnchor}
              closePeek={closePeek}
              handleOpenPeekInResultsAndClose={handleOpenPeekInResultsAndClose}
              handleExecuteFromSelectionBar={handleExecuteFromSelectionBar}
              handleExecuteFromCursorBar={handleExecuteFromCursorBar}
              handlePeek={handlePeek}
              onOpenVisualView={handleOpenVisualView}
            />}
          </div>

          {/* Resizable divider (shared with the Visual Query Editor tab). */}
          <ResultsPaneDivider
            topPercent={topHeight}
            isDragging={isDividerDragging}
            onPointerDown={handlePointerDown}
            onDoubleClick={handleDividerDoubleClick}
            onKeyDown={handleDividerKeyDown}
            ariaLabel="Resize editor and results panes"
            topPaneName="editor"
            testId="results-pane-divider"
          />

          {/* Bottom section - Results/Messages + Tabs + Status */}
          <div
            className="flex-1 min-h-0 flex flex-col overflow-hidden"
            style={{
              height: bottomPaneCollapsed
                ? `${tabsBarHeight + statusBarHeight}px`
                : `calc(${100 - topHeight}% - ${dividerHeight / 2}px)`,
            }}
          >
            <ResultsPanel
              state={resultsPanelState}
              tabId={tabId}
              content={tabContent}
              connectionId={connectionId}
              database={tabDatabase}
              engineId={engineId}
              isExecuting={isExecuting}
              isCancelling={isCancelling}
              isLongRunning={isLongRunning}
              onCancel={cancelCurrentQuery}
              onReExecute={handleResultsGridReExecute}
              displayMode={resultDisplayMode}
              editMode={editModeProp}
              onAddToWhere={handleAddToWhere}
              addColumnToFilter={addColumnToFilter}
              totalRowCount={totalRowCount}
              collapseShortcutHint={collapseShortcutHint}
              tabsBarHeight={tabsBarHeight}
              onPinToDashboard={connectionId && dashboardsBetaEnabled ? handlePinToDashboard : undefined}
              onExplainError={connectionId ? handleExplainError : undefined}
              onMessageAction={connectionId ? handleMessageAction : undefined}
              aiCliAvailable={aiTaskCliAvailable}
              aiCliDetectionLoading={aiTaskCliDetectionLoading}
              staleNotice={
                viewMode.shownStatementSql != null &&
                lastExecutedSql != null &&
                lastExecutedSql.trim() !== viewMode.shownStatementSql
                  ? vqeCopy.workspace.resultsStale
                  : undefined
              }
            />

            {/* Status Bar - fixed at bottom */}
            <div className="flex-shrink-0" style={{ height: statusBarHeight }}>
              <QueryStatusBar
                message={populationEntry?.state === 'populating'
                  ? (populationEntry.finalRowCount != null
                    ? `Loading... ${populationEntry.loadedRowCount.toLocaleString()} / ${populationEntry.finalRowCount.toLocaleString()} rows`
                    : `Loading... ${populationEntry.loadedRowCount.toLocaleString()} rows`)
                  : lastMessage}
                tabId={tabId}
                filePath={filePath}
                isFileDirty={isFileDirty}
                connectionId={connectionId}
                connectionName={connectionInfo?.connectionName}
                serverHost={connectionInfo?.serverHost}
                database={connectionInfo?.database}
                executionTime={executionTime}
                isLongRunning={isLongRunning}
                // Gates the elapsed-time section (c840986bb: hidden until the
                // tab has run its first query). Threading it was lost when
                // `QueryEditorTab` was split into this component, which left
                // the prop at its `false` default and the timer permanently
                // hidden.
                hasExecuted={hasExecuted}
                rowCount={
                  // When the tab has no result sets (e.g. after a connection swap clears
                  // them), show 0 — don't linger on the previous query's hook state.
                  tabContent?.resultSets === undefined
                    ? 0
                    : isWindowed
                      ? (activeRowCount ?? rowCount)
                      : rowCount
                }
                totalRowCount={tabContent?.resultSets === undefined ? undefined : totalRowCount}
                affectedRowCount={tabContent?.resultSets === undefined ? undefined : affectedRowCount}
                scrollRow={isWindowed ? scrollRow : undefined}
                isTruncated={isTruncated}
                onDatabaseChange={handleDatabaseChange}
                databaseSelectorOpenNonce={databaseSelectorOpenNonce}
                onConnectionChange={handleConnectionChange}
                onAddConnection={() => useConnectionsUIStore.getState().setShowNewConnectionDialog(true)}
                // While a manual refresh runs for this tab's pair the pill
                // shows the spinner regardless of provider status events: the
                // MSSQL provider re-emits `ready` on every phase-1 / phase-2
                // republish, which would end the spinner long before the
                // refresh does.
                intelliSenseStatus={isReloadingSchema ? 'initializing' : intelliSenseStatus}
                intelliSenseMessage={isReloadingSchema ? INTELLISENSE_REFRESHING_MESSAGE : intelliSenseMessage}
                onReloadIntelliSenseSchema={reloadSchema}
                isReloadingIntelliSenseSchema={isReloadingSchema}
                transactionMode={tabContent.transactionMode}
                transactionState={tabContent.transactionState}
                connectionColor={connectionColor}
              />
            </div>
          </div>
        </div>

        {/* AI Chat Sidebar */}
        {isAiChatOpen && (
          <AiChatSidebar
            tabId={tabId}
            onClose={handleCloseAiChat}
            parentWidth={parentWidth}
            databaseObjects={databaseObjects}
            onSendMessage={aiChatSession.sendMessage}
            onCancel={aiChatSession.cancel}
            connectionId={connectionId}
            database={tabDatabase}
            engine={engineId}
          />
        )}
      </div>

      {/* Dialog layer: dangerous-query, external-file-change, database-switch
          transaction warning, results-edit preview, AI-chat CLI required */}
      <QueryEditorTabDialogs
        dangerInfo={dangerInfo}
        dangerousDialogOpen={dangerousDialogOpen}
        handleDangerousConfirm={handleDangerousConfirm}
        handleDangerousCancel={handleDangerousCancel}
        isActive={isActive}
        pendingChange={pendingChange}
        filePath={filePath}
        isFileDirty={isFileDirty}
        handleExternalKeep={handleExternalKeep}
        handleExternalReload={handleExternalReload}
        pendingDatabaseChange={pendingDatabaseChange}
        setPendingDatabaseChange={setPendingDatabaseChange}
        handleConfirmDatabaseChange={handleConfirmDatabaseChange}
        connectionId={connectionId}
        tabDatabase={tabDatabase}
        isPreviewDialogOpen={isPreviewDialogOpen}
        setIsPreviewDialogOpen={setIsPreviewDialogOpen}
        resultsEditing={resultsEditing}
        handleOpenInQueryEditor={handleOpenInQueryEditor}
        transactionMode={showTransactionControls ? tabContent.transactionMode : undefined}
        transactionState={tabContent.transactionState}
        showCliRequiredDialog={showCliRequiredDialog}
        aiTaskCliRequiredOpen={aiTaskCliRequiredOpen}
        setAiTaskCliRequiredOpen={setAiTaskCliRequiredOpen}
        setShowCliRequiredDialog={setShowCliRequiredDialog}
      />
    </div>
  );
}
